import React, { useState, useEffect, useCallback } from 'react';
import { X, FileText, Database, Target, Table, Save } from 'lucide-react';
import { CreatePublicationRequest, DataSource } from '../../types';
import { publicationAPI, dataSourcesAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface NewPublicationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const NewPublicationModal: React.FC<NewPublicationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<CreatePublicationRequest>({
    title: '',
    abstract: '',
    methods: '',
    expectedResults: '',
    dataSources: [],
    variablesUsed: '',
    tablesDescription: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [isLoadingDataSources, setIsLoadingDataSources] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const draftKey = 'new_publication_draft_v1';

  useEffect(() => {
    if (isOpen) {
      loadDataSources();
      // Restore draft
      try {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          setFormData(prev => ({
            ...prev,
            ...parsed,
            dataSources: Array.isArray(parsed?.dataSources) ? parsed.dataSources : []
          }));
        }
      } catch (error) {
        console.error('Error loading draft:', error);
      }
    }
  }, [isOpen]);

  // Function to save draft
  const saveDraft = useCallback(() => {
    try {
      // Only save if there's actual content
      const hasContent = formData.title.trim() || 
                        formData.abstract.trim() || 
                        formData.methods.trim() || 
                        (formData.expectedResults?.trim() || '') ||
                        (formData.variablesUsed?.trim() || '') ||
                        (formData.tablesDescription?.trim() || '') ||
                        formData.dataSources.length > 0;
      
      if (hasContent) {
        localStorage.setItem(draftKey, JSON.stringify(formData));
        setDraftSaved(true);
        // Hide the "draft saved" indicator after 2 seconds
        setTimeout(() => setDraftSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, [formData, draftKey]);

  // Autosave draft when formData changes (with debouncing)
  useEffect(() => {
    const timeoutId = setTimeout(saveDraft, 500); // Debounce for 500ms
    return () => clearTimeout(timeoutId);
  }, [formData]);

  // Save draft when window loses focus (user navigates away)
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveDraft();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveDraft();
      }
    };

    if (isOpen) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, formData]);

  const loadDataSources = async () => {
    try {
      setIsLoadingDataSources(true);
      const sources = await dataSourcesAPI.getDataSources();
      setDataSources(sources);
    } catch (error) {
      console.error('Failed to load data sources:', error);
      toast.error('Failed to load data sources');
    } finally {
      setIsLoadingDataSources(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDataSourceChange = (dataSourceId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      dataSources: checked 
        ? [...prev.dataSources, dataSourceId]
        : prev.dataSources.filter(id => id !== dataSourceId)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    // Client-side validation to match backend rules
    const newErrors: Record<string, string> = {};
    if (!formData.title || formData.title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters.';
    }
    if (!formData.abstract || formData.abstract.trim().length < 50) {
      newErrors.abstract = 'Abstract must be at least 50 characters.';
    }
    if (!formData.methods || formData.methods.trim().length < 10) {
      newErrors.methods = 'Methods must be at least 10 characters.';
    }
    if (!formData.dataSources || formData.dataSources.length === 0) {
      newErrors.dataSources = 'Please select at least one data source.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      await publicationAPI.createPublication(formData);
      
      // Clear cache to ensure fresh data
      const { cacheService } = await import('../../services/cache');
      cacheService.clearPattern('/dashboard/batch');
      cacheService.clearPattern('/admin/batch');
      
      toast.success('Publication submitted successfully! It will be reviewed by the committee soon.');
      onSuccess();
      onClose();
      // Clear draft on success
      try { localStorage.removeItem(draftKey); } catch {}
      setFormData({
        title: '',
        abstract: '',
        methods: '',
        expectedResults: '',
        dataSources: [],
        variablesUsed: '',
        tablesDescription: '',
      });
    } catch (error: any) {
      // Show detailed validation errors from backend if present
      const apiErrors = error?.response?.data?.errors as Array<{ msg: string; param?: string }>|undefined;
      if (apiErrors && apiErrors.length) {
        const fieldErrors: Record<string, string> = {};
        apiErrors.forEach(e => {
          if (e.param) fieldErrors[e.param] = e.msg;
        });
        setErrors(fieldErrors);
        toast.error('Please fix the highlighted fields.');
      } else {
        toast.error(error.response?.data?.error || 'Failed to submit publication');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-bold text-gray-900">Submit New Publication Idea</h2>
            {draftSaved && (
              <div className="flex items-center space-x-1 text-green-600 text-sm">
                <Save className="h-4 w-4" />
                <span>Draft saved</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Auto-save notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 text-blue-700 text-sm">
              <Save className="h-4 w-4" />
              <span>Your work is automatically saved as you type. You can safely navigate away and return later.</span>
            </div>
          </div>

          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
              <FileText className="inline h-4 w-4 mr-1" />
              Publication Title *
            </label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              minLength={5}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Enter your research title"
            />
            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
          </div>

          {/* Abstract */}
          <div>
            <label htmlFor="abstract" className="block text-sm font-medium text-gray-700 mb-2">
              Abstract *
            </label>
            <textarea
              id="abstract"
              name="abstract"
              value={formData.abstract}
              onChange={handleInputChange}
              required
              rows={4}
              minLength={50}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Brief description of your research (minimum 50 characters). If the abstract not ready, please provide background information or expectations."
            />
            {errors.abstract && <p className="mt-1 text-sm text-red-600">{errors.abstract}</p>}
          </div>

          {/* Methods */}
          <div>
            <label htmlFor="methods" className="block text-sm font-medium text-gray-700 mb-2">
              Proposed Methods *
            </label>
            <textarea
              id="methods"
              name="methods"
              value={formData.methods}
              onChange={handleInputChange}
              required
              rows={3}
              minLength={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Describe your methodology"
            />
            {errors.methods && <p className="mt-1 text-sm text-red-600">{errors.methods}</p>}
          </div>

          {/* Data Sources */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Database className="inline h-4 w-4 mr-1" />
              Data Sources * (Select all that apply)
            </label>
            {isLoadingDataSources ? (
              <div className="text-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-600">Loading data sources...</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                {dataSources.map((source) => (
                  <label key={source.id} className="flex items-start space-x-3 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={formData.dataSources.includes(source.id)}
                      onChange={(e) => handleDataSourceChange(source.id, e.target.checked)}
                      className="mt-1 h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">{source.name}</div>
                      {source.description && (
                        <div className="text-xs text-gray-500 mt-1">{source.description}</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
            {errors.dataSources && <p className="mt-1 text-sm text-red-600">{errors.dataSources}</p>}
          </div>

          {/* Expected Results */}
          <div>
            <label htmlFor="expectedResults" className="block text-sm font-medium text-gray-700 mb-2">
              <Target className="inline h-4 w-4 mr-1" />
              Expected Results
            </label>
            <textarea
              id="expectedResults"
              name="expectedResults"
              value={formData.expectedResults}
              onChange={handleInputChange}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="What do you expect to find?"
            />
          </div>

          {/* Variables Used */}
          <div>
            <label htmlFor="variablesUsed" className="block text-sm font-medium text-gray-700 mb-2">
              Variables to be Used
            </label>
            <textarea
              id="variablesUsed"
              name="variablesUsed"
              value={formData.variablesUsed}
              onChange={handleInputChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="List the key variables you plan to analyze"
            />
          </div>

          {/* Tables Description */}
          <div>
            <label htmlFor="tablesDescription" className="block text-sm font-medium text-gray-700 mb-2">
              <Table className="inline h-4 w-4 mr-1" />
              Tables Description
            </label>
            <textarea
              id="tablesDescription"
              name="tablesDescription"
              value={formData.tablesDescription}
              onChange={handleInputChange}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              placeholder="Describe the tables you plan to create"
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? 'Submitting...' : 'Submit for Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPublicationModal;
